import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { MeterReading } from "@/api/districts/types";
import { ReadingStatusBadge } from "@/components/mqtt/reading-status-badge";

const PAGE_SIZE = 20;

function fmtDt(dt: string | null | undefined) {
	if (!dt) return "—";
	return new Intl.DateTimeFormat("pl", {
		dateStyle: "short",
		timeStyle: "short",
	}).format(new Date(dt));
}

export function MeterReadingsCard({ readings }: { readings: MeterReading[] }) {
	const { t } = useTranslation();
	const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
	const sentinelRef = useRef<HTMLDivElement | null>(null);
	const scrollRef = useRef<HTMLDivElement | null>(null);

	const visible = readings.slice(0, visibleCount);
	const hasMore = visibleCount < readings.length;

	useEffect(() => {
		if (!hasMore) return;
		const sentinel = sentinelRef.current;
		const root = scrollRef.current;
		if (!sentinel || !root) return;
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting) {
					setVisibleCount((c) => Math.min(c + PAGE_SIZE, readings.length));
				}
			},
			{ root, rootMargin: "0px 0px 100px 0px" },
		);
		observer.observe(sentinel);
		return () => observer.disconnect();
	}, [hasMore, readings.length]);

	return (
		<div className="flex flex-col gap-3">
			<h3 className="text-base font-semibold">
				{t("meterDetail.lastReadings")}
			</h3>
			{readings.length === 0 ? (
				<p className="text-muted-foreground py-6 text-center text-sm">
					{t("meterDetail.noReadings")}
				</p>
			) : (
				<div className="rounded-md border">
					<table className="w-full table-fixed text-sm">
						<colgroup>
							<col className="w-1/2" />
							<col className="w-1/2" />
						</colgroup>
						<thead>
							<tr className="border-b bg-muted/50">
								<th className="px-4 py-3 text-left font-medium">
									{t("meterDetail.colDate")}
								</th>
								<th className="px-4 py-3 text-left font-medium">
									{t("meterDetail.colStatus")}
								</th>
							</tr>
						</thead>
					</table>
					<div ref={scrollRef} className="max-h-96 overflow-y-auto">
						<table className="w-full table-fixed text-sm">
							<colgroup>
								<col className="w-1/2" />
								<col className="w-1/2" />
							</colgroup>
							<tbody>
								{visible.map((r) => (
									<tr key={r.id} className="border-b last:border-0">
										<td className="px-4 py-3 font-mono tabular-nums text-muted-foreground">
											{fmtDt(r.reading_date)}
										</td>
										<td className="px-4 py-3">
											<ReadingStatusBadge status={r.status} />
										</td>
									</tr>
								))}
							</tbody>
						</table>
						{hasMore && <div ref={sentinelRef} className="h-4" />}
					</div>
				</div>
			)}
		</div>
	);
}
